import { ReactNode, useCallback, useMemo } from "react";
import classNames from "classnames";
import { noop } from "@common/helpers/function";
import { useScope } from "@state/scopeContext.lib";
import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useWorkspacesList } from "@api/workspace.api";
import { MembersIcon, ActivityIcon, TriangleIcon } from "@icons";
import { AggregatedFilters } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters";
import { AggregatedFiltersOverlay } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersOverlay";
import { AggregatedFiltersProvider } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFiltersProvider";
import { useAggregatedFiltersContext } from "@components/pageHeader/filters/AggregatedFilters/AggregatedFilters.context";
import { ActiveFilterButton } from "@components/pageHeader/filters/ActiveFilters/ActiveFilterButton";
import { ActiveFilters } from "@components/pageHeader/filters/ActiveFilters/ActiveFilters";
import { FilterNavTab } from "@components/pageHeader/filters/FilterNavTab";
import { FilterTabPanel } from "@components/pageHeader/filters/FilterTabPanel";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import overlayStyles from "@components/pageHeader/filters/FiltersOverlay.module.scss";
import statusStyles from "@components/pageHeader/filters/Tabs/TransactionStatusFilter/TransactionStatusFilter.module.scss";

const MEMBER_TAB = "member";
const CLIENT_TAB = "client";

type AccessLogFilterTab = typeof MEMBER_TAB | typeof CLIENT_TAB;

const AccessLogFilterNavTab = ({
    tabKey,
    label,
    icon,
}: Readonly<{
    tabKey: AccessLogFilterTab;
    label: string;
    icon: ReactNode;
}>) => (
    <FilterNavTab eventKey={tabKey} className={overlayStyles.navigationButton}>
        <span className={overlayStyles.navigationItem}>
            <span className={overlayStyles.navigationIcon}>{icon}</span>
            {label}
            <TriangleIcon
                className={classNames(
                    overlayStyles.navigationActiveIndicator,
                    "icon-color-gray-500",
                )}
            />
        </span>
    </FilterNavTab>
);

interface MemberAccessLogFiltersControlsProps {
    memberId: number | undefined;
    clientWorkspaceId: string | undefined;
    onMemberIdChange: (memberId: number | undefined) => void;
    onClientWorkspaceIdChange: (clientWorkspaceId: string | undefined) => void;
}

/**
 * "Add filter" popover + active-filter chips for the org member access log
 * table. The backend query only accepts a single `memberId` / `clientWorkspaceId`
 * (this is an audit log read, not a multi-value list filter), so each tab uses
 * `FilterMultiselect` but callers are constrained to at most one selected
 * value: picking a second value replaces the first rather than adding to it.
 */
export const MemberAccessLogFiltersControls = ({
    memberId,
    clientWorkspaceId,
    onMemberIdChange,
    onClientWorkspaceIdChange,
}: MemberAccessLogFiltersControlsProps) => {
    const { organizationId } = useScope();
    const { data: members } = useCurrentOrganizationMembers();
    const { data: clients } = useWorkspacesList(
        organizationId ? { organizationId } : undefined,
        { enabled: !!organizationId },
    );

    const memberOptions: FilterMultiselectOption[] = useMemo(
        () =>
            (members ?? []).map((member) => ({
                value: member.user.id,
                label: member.user.name,
                searchableLabel: member.user.email
                    ? `${member.user.name} ${member.user.email}`
                    : member.user.name,
            })),
        [members],
    );

    const clientOptions: FilterMultiselectOption[] = useMemo(
        () =>
            (clients ?? []).map((client) => ({
                value: client.id,
                label: client.name,
                searchableLabel: client.name,
            })),
        [clients],
    );

    const memberValue = useMemo(
        () => (memberId !== undefined ? [memberId] : []),
        [memberId],
    );
    const clientValue = useMemo(
        () => (clientWorkspaceId !== undefined ? [clientWorkspaceId] : []),
        [clientWorkspaceId],
    );

    const activeFilters = useMemo<AccessLogFilterTab[]>(() => {
        const active: AccessLogFilterTab[] = [];
        if (memberId !== undefined) {
            active.push(MEMBER_TAB);
        }
        if (clientWorkspaceId !== undefined) {
            active.push(CLIENT_TAB);
        }
        return active;
    }, [memberId, clientWorkspaceId]);

    // Only the most recently picked value is kept — the last element of the
    // multiselect's selection list — matching the single-value backend filter.
    const handleMemberValuesChange = useCallback(
        (values: FilterMultiselectValue[]) => {
            const last = values[values.length - 1];
            onMemberIdChange(typeof last === "number" ? last : undefined);
        },
        [onMemberIdChange],
    );

    const handleClientValuesChange = useCallback(
        (values: FilterMultiselectValue[]) => {
            const last = values[values.length - 1];
            onClientWorkspaceIdChange(
                typeof last === "string" ? last : undefined,
            );
        },
        [onClientWorkspaceIdChange],
    );

    const clearFilters = useCallback(() => {
        onMemberIdChange(undefined);
        onClientWorkspaceIdChange(undefined);
    }, [onMemberIdChange, onClientWorkspaceIdChange]);

    const resetMember = useCallback(
        () => onMemberIdChange(undefined),
        [onMemberIdChange],
    );

    const resetClient = useCallback(
        () => onClientWorkspaceIdChange(undefined),
        [onClientWorkspaceIdChange],
    );

    const memberLabel = useMemo(
        () => memberOptions.find((option) => option.value === memberId)?.label,
        [memberOptions, memberId],
    );
    const clientLabel = useMemo(
        () =>
            clientOptions.find((option) => option.value === clientWorkspaceId)
                ?.label,
        [clientOptions, clientWorkspaceId],
    );

    return (
        <AggregatedFiltersProvider defaultTab={MEMBER_TAB}>
            <AggregatedFilters label="Add filter">
                <AggregatedFiltersOverlay
                    filterClassname={overlayStyles.filter}
                    navigation={
                        <>
                            <AccessLogFilterNavTab
                                tabKey={MEMBER_TAB}
                                label="Member"
                                icon={<MembersIcon />}
                            />
                            <AccessLogFilterNavTab
                                tabKey={CLIENT_TAB}
                                label="Client"
                                icon={<ActivityIcon />}
                            />
                        </>
                    }
                    content={
                        <>
                            <FilterTabPanel eventKey={MEMBER_TAB}>
                                <div className={statusStyles.filter}>
                                    <FilterMultiselect
                                        inDropdown={false}
                                        options={memberOptions}
                                        value={memberValue}
                                        onChange={handleMemberValuesChange}
                                        name="memberId"
                                        onBlur={noop}
                                    />
                                </div>
                            </FilterTabPanel>
                            <FilterTabPanel eventKey={CLIENT_TAB}>
                                <div className={statusStyles.filter}>
                                    <FilterMultiselect
                                        inDropdown={false}
                                        options={clientOptions}
                                        value={clientValue}
                                        onChange={handleClientValuesChange}
                                        name="clientWorkspaceId"
                                        onBlur={noop}
                                    />
                                </div>
                            </FilterTabPanel>
                        </>
                    }
                />
            </AggregatedFilters>

            {activeFilters.length > 0 && (
                <ActiveFilters
                    activeFilters={activeFilters}
                    maxVisibleFilters={activeFilters.length}
                    clearFilters={clearFilters}
                    renderVisibleFilters={(visibleFilters) =>
                        visibleFilters.map((tabKey) =>
                            tabKey === MEMBER_TAB ? (
                                <MemberAccessLogActiveFilterChip
                                    key={tabKey}
                                    tabKey={MEMBER_TAB}
                                    label={memberLabel}
                                    onReset={resetMember}
                                />
                            ) : (
                                <MemberAccessLogActiveFilterChip
                                    key={tabKey}
                                    tabKey={CLIENT_TAB}
                                    label={clientLabel}
                                    onReset={resetClient}
                                />
                            ),
                        )
                    }
                />
            )}
        </AggregatedFiltersProvider>
    );
};

const MemberAccessLogActiveFilterChip = ({
    tabKey,
    label,
    onReset,
}: Readonly<{
    tabKey: AccessLogFilterTab;
    label: ReactNode;
    onReset: () => void;
}>) => {
    const { open } = useAggregatedFiltersContext();

    if (label == null) {
        return null;
    }

    return (
        <ActiveFilterButton
            openFilter={() => open(tabKey)}
            resetFilter={onReset}
            label={label}
        />
    );
};
